/**
 * @param {object} Drupal - The Drupal object.
 * @param {object} Vimeo - The Vimeo Player library.
 */
(function (Drupal, Vimeo) {
  'use strict';

  Drupal.behaviors.heroBanner = {
    attach: function (context, settings) {
      const MAX_LOAD_TIME_MS = 5000;

      const createScrollAnimationHandler = (el) => {
        let ticking = false;
        let cachedOffsetTop = null;
        let cachedViewportHeight = window.innerHeight;
        let lastOpacityValue = null;

        // Cache expensive layout calculations
        const updateCache = () => {
          cachedOffsetTop = el.offsetTop;
          cachedViewportHeight = window.innerHeight;
        };

        const animate = () => {
          const rect = el.getBoundingClientRect();
          const progress = 1 - ((rect.top + cachedOffsetTop) / cachedViewportHeight);
          const newOpacity = Math.max(0, Math.min(1, progress));

          // Only update if value changed significantly (avoid subpixel updates)
          if (lastOpacityValue === null || Math.abs(newOpacity - lastOpacityValue) > 0.01) {
            el.style.setProperty('--hero-scrim-opacity', newOpacity);
            lastOpacityValue = newOpacity;
          }

          ticking = false;
        };

        const onScroll = () => {
          if (!ticking) {
            window.requestAnimationFrame(animate);
            ticking = true;
          }
        };

        // Initial cache
        updateCache();

        // Recalculate on resize
        let resizeTimeout;
        window.addEventListener('resize', () => {
          if (resizeTimeout) {
            window.cancelAnimationFrame(resizeTimeout);
          }
          resizeTimeout = window.requestAnimationFrame(updateCache);
        }, {passive: true});

        return {onScroll, animate};
      };

      // Logic to instantiate video and handle events such as resize and interaction
      // with custom controls, if present.
      const initializeVideo = (videoContainer, videoPlayButton, videoMuteButton) => {
        const videoUrl = videoContainer.getAttribute('data-video-url');
        const videoTitle = videoContainer.getAttribute('data-video-title');
        const mobileVideoUrl = videoContainer.getAttribute('data-mobile-video-url');
        const mobileVideoTitle = videoContainer.getAttribute('data-mobile-video-title');

        const isMobile = () => {
          return window.innerWidth < 768;
        };

        const videoSrc = mobileVideoUrl && isMobile() ? mobileVideoUrl : videoUrl;
        const videoTitleText = mobileVideoUrl && isMobile() ? mobileVideoTitle : videoTitle;

        const iframe = document.createElement('iframe');
        iframe.className = 'hero-banner__video';
        iframe.src = videoSrc;
        iframe.allow = 'autoplay';
        iframe.title = videoTitleText;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.allowFullscreen = true;
        iframe.setAttribute('tabindex', '-1');
        iframe.setAttribute('aria-hidden', 'true');

        videoContainer.appendChild(iframe);

        // Perform operations on window resize.
        window.addEventListener('resize', () => {
          // Update player width.
          videoContainer.width = window.innerWidth;

          // If a mobile video is defined and the current video is not
          // appropriate for the current viewport, update the video source.
          if (mobileVideoUrl) {
            if (isMobile() && iframe.src !== mobileVideoUrl) {
              iframe.src = mobileVideoUrl;
            }
            else if (!isMobile() && iframe.src !== videoUrl) {
              iframe.src = videoUrl;
            }
          }
        });

        // Handle programmatic controls and user reduced motion preference.
        // Vimeo library is provided by component library dependency
        // https://github.com/vimeo/player.js
        // eslint-disable-next-line no-undef
        const player = new Vimeo.Player(iframe, {
          width: window.innerWidth,
          background: true,
          controls: false,
          muted: true,
          loop: true,
          autoplay: true
        });

        videoContainer.dataset.loadState = 'loaded';

        // Set control states
        if (videoPlayButton) {
          videoContainer.dataset.playState = 'playing';
          videoPlayButton.setAttribute('aria-pressed', 'false');
          videoPlayButton.setAttribute('aria-label', videoPlayButton.dataset.toggleLabel);
        }

        if (videoMuteButton) {
          player.setMuted(true).then(() => {
            videoContainer.dataset.mutedState = 'muted';
            videoMuteButton.setAttribute('aria-pressed', false);
            videoMuteButton.setAttribute('aria-label', videoMuteButton.dataset.toggleLabel);
          });
        }

        /* eslint-ignore-next-line */
        player.on('bufferstart', function onBufferStart(data) {
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            if (videoPlayButton) {
              videoContainer.dataset.playState = 'paused';
              videoPlayButton.setAttribute('aria-pressed', 'true');
              videoPlayButton.setAttribute('aria-label', 'Play video');
            }
            player.pause();
            return;
          }

          videoContainer.dataset.loadState = 'buffering';
        });


        if (videoPlayButton) {
          // Create live region for announcing state changes
          const liveRegion = document.createElement('div');
          liveRegion.setAttribute('role', 'status');
          liveRegion.setAttribute('aria-live', 'assertive');
          liveRegion.setAttribute('aria-atomic', 'true');
          liveRegion.className = 'util-visually-hidden';
          videoContainer.appendChild(liveRegion);

          // Helper to announce state changes
          const announce = (message) => {
            liveRegion.textContent = message;
          };

          // Track state manually for synchronous updates
          let isPlaying = true;

          // Listen to Vimeo player events only for external state changes
          player.on('play', function () {
            if (!isPlaying) {
              isPlaying = true;
              videoContainer.dataset.playState = 'playing';
              videoPlayButton.setAttribute('aria-pressed', 'false');
              videoPlayButton.setAttribute('aria-label', videoPlayButton.dataset.toggleLabel);
              announce('Playing, Pause video button');
            }
          });

          player.on('pause', function () {
            if (isPlaying) {
              isPlaying = false;
              videoContainer.dataset.playState = 'paused';
              videoPlayButton.setAttribute('aria-pressed', 'true');
              videoPlayButton.setAttribute('aria-label', 'Play video');
              announce('Paused, Play video button');
            }
          });

          // pause/play button behavior
          videoPlayButton.addEventListener('click', function (event) {
            // Update UI immediately in sync before any async operations
            if (isPlaying) {
              isPlaying = false;
              videoContainer.dataset.playState = 'paused';
              videoPlayButton.setAttribute('aria-pressed', 'true');
              videoPlayButton.setAttribute('aria-label', 'Play video');
              announce('Paused, Play video button');
              player.pause();
            }
            else {
              isPlaying = true;
              videoContainer.dataset.playState = 'playing';
              videoPlayButton.setAttribute('aria-pressed', 'false');
              videoPlayButton.setAttribute('aria-label', videoPlayButton.dataset.toggleLabel);
              announce('Playing, Pause video button');
              player.play();
            }
          });
        }

        if (videoMuteButton) {
          // Create live region for announcing mute state changes
          const muteLiveRegion = document.createElement('div');
          muteLiveRegion.setAttribute('role', 'status');
          muteLiveRegion.setAttribute('aria-live', 'assertive');
          muteLiveRegion.setAttribute('aria-atomic', 'true');
          muteLiveRegion.className = 'util-visually-hidden';
          videoContainer.appendChild(muteLiveRegion);

          const announceMute = (message) => {
            muteLiveRegion.textContent = message;
          };

          let isMuted = true;

          videoMuteButton.addEventListener('click', function (event) {
            if (isMuted) {
              isMuted = false;
              videoContainer.dataset.mutedState = 'unmuted';
              videoMuteButton.setAttribute('aria-pressed', 'true');
              videoMuteButton.setAttribute('aria-label', 'Mute audio');
              announceMute('Unmuted, Mute audio button');
              player.setMuted(false).then(function () {
                player.setVolume(1);
              });
            }
            else {
              isMuted = true;
              videoContainer.dataset.mutedState = 'muted';
              videoMuteButton.setAttribute('aria-pressed', 'false');
              videoMuteButton.setAttribute('aria-label', videoMuteButton.dataset.toggleLabel);
              announceMute('Muted, Unmute audio button');
              player.setMuted(true);
            }
          });
        }
      };

      /* eslint-disable max-nested-callbacks */
      once('js-hero-banner', '.hero-banner', context).forEach(function (component) {
        const videoContainer = component.querySelector('.hero-banner__video-container');
        const videoPlayButton = component.querySelector('.video-controls-button--play');
        const videoMuteButton = component.querySelector('.video-controls-button--sound');

        // Initialize scroll animation.
        if (!CSS.supports('animation-timeline', 'scroll()')) {
          const {onScroll, animate} = createScrollAnimationHandler(component);
          window.addEventListener('scroll', onScroll, {passive: true});
          // Set initial state
          animate();
        }

        // If the video container is not present, we don't need to execute
        // the logic for video instantiation and playback.
        if (!videoContainer) {
          return;
        }

        // Instantiate the video only if the page took less than 5 seconds
        // to load.
        window.addEventListener('load', function () {
          // Get the PerformanceNavigationTiming entry
          // The getEntriesByType('navigation') method returns an array,
          // and for a standard page load, there should be only one entry.
          const [navigationEntry] = performance.getEntriesByType('navigation');

          if (navigationEntry) {
            // Calculate the elapsed time from fetchStart to loadEventEnd
            // fetchStart is typically when the browser starts fetching the
            // document. loadEventEnd is when the 'load' event finishes. The
            // requestAnimationFrame() is a Safari (all devices) conceit as the
            // loadEventEnd value is not always available immediately after
            // the event occurs on that browser.
            requestAnimationFrame(() => {
              // One last check for a valid load time
              if (navigationEntry.loadEventEnd > 0) {
                // Calculate time for page to load, from initial fetch to finished loading
                const totalLoadTime = navigationEntry.loadEventEnd - navigationEntry.fetchStart;

                // Initialize video if within the threshold
                if (totalLoadTime <= MAX_LOAD_TIME_MS) {
                  initializeVideo(videoContainer, videoPlayButton, videoMuteButton);
                }
              }
            });
          }
        }, {once: true}); // end window.addEventListener('load')
      }); // end once()
    }
  };

})(Drupal, window.Vimeo);
